import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Sparkles,
  Heart,
  Award,
  Save,
  Trash2,
  AlertCircle,
  Eye,
  X,
} from 'lucide-react';
import { useSkillTests } from '../../hooks/useSkillTests';

export interface EditableJob {
  id: number;
  title: string;
  location: string;
  salary: string;
  type: string;
  posted: string;
  applicants: number;
  views: number;
  status: string;
  description?: string;
  requirements?: string[];
  benefits?: string[];
  returnerFriendly?: boolean;
  skillTestId?: string;
}

interface EditJobPostingProps {
  job: EditableJob;
  onClose: () => void;
  onSave: (updatedJob: EditableJob) => void;
  onDelete?: (jobId: number) => void;
  allowDelete?: boolean;
  primaryActionLabel?: string;
  successMessage?: string;
  deleteMessage?: string;
  heading?: string;
  subheading?: string;
}

export function EditJobPosting({
  job,
  onClose,
  onSave,
  onDelete,
  allowDelete,
  primaryActionLabel,
  successMessage,
  deleteMessage,
  heading,
  subheading,
}: EditJobPostingProps) {
  const { tests: skillTests } = useSkillTests();
  const [formData, setFormData] = useState({
    title: job.title || '',
    location: job.location || '',
    type: job.type || 'full-time',
    minSalary: job.salary?.split('-')[0]?.replace(/[£,\s]/g, '') || '',
    maxSalary: job.salary?.split('-')[1]?.replace(/[£,\s]/g, '') || '',
    description: job.description || '',
    requirements: job.requirements || [],
    benefits: job.benefits || [],
    returnerFriendly: job.returnerFriendly || false,
    skillTestId: job.skillTestId || '',
    status: job.status || 'Active',
  });

  const showDelete = (allowDelete ?? Boolean(onDelete)) && Boolean(onDelete);

  const [useAI, setUseAI] = useState(false);
  const [newRequirement, setNewRequirement] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData((prev) => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()],
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    setFormData((prev) => ({
    ...prev,
    requirements: prev.requirements.filter((_, i) => i !== index),
  }));
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData((prev) => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()],
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a job title');
      return;
    }
    if (!formData.location.trim()) {
      toast.error('Please enter a location');
      return;
    }

    const updatedJob: EditableJob = {
      ...job,
      title: formData.title,
      location: formData.location,
      type: formData.type,
      salary: `£${formData.minSalary} - £${formData.maxSalary}`,
      description: formData.description,
      requirements: formData.requirements,
      benefits: formData.benefits,
      returnerFriendly: formData.returnerFriendly,
      skillTestId: formData.skillTestId,
      status: formData.status,
    };

    onSave(updatedJob);
    toast.success(successMessage ?? 'Job posting updated successfully!');
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(job.id);
      setIsDeleting(false);
      toast.success(deleteMessage ?? 'Job posting deleted');
    }
  };

  const generateWithAI = () => {
    setGenerating(true);
    toast.loading('AI is generating job description...');

    setTimeout(() => {
      const aiDescription = `We are seeking a talented ${formData.title} to join our team in ${formData.location}.\n\nThis is a ${formData.type} position offering competitive compensation ranging from £${formData.minSalary} to £${formData.maxSalary}.\n\nKey Responsibilities:\n• Provide excellent service and support\n• Work collaboratively with team members\n• Maintain high standards of professionalism\n• Contribute to positive team culture\n\nWhat We Offer:\n• Competitive salary package\n• Professional development opportunities\n• Supportive work environment\n• Work-life balance\n\nWe welcome applications from all qualified candidates, including those returning to work after a career break.`;

      setFormData((prev) => ({ ...prev, description: aiDescription }));
      setGenerating(false);
      toast.dismiss();
      toast.success('Job description generated!');
    }, 900);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-white mb-1">{heading ?? 'Edit Job Posting'}</h3>
          <p className="text-sm text-gray-400">{subheading ?? 'Update job details and requirements'}</p>
        </div>
        <Badge
          className={
            formData.status === 'Active'
              ? 'bg-green-500/20 text-green-400 border-green-500/50'
              : 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
          }
        >
          {formData.status}
        </Badge>
      </div>

      <div className="space-y-6">
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Job Title</Label>
              <Input
                value={formData.title}
                onChange={(event) => handleInputChange('title', event.target.value)}
                placeholder="e.g. Senior Care Assistant"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(event) => handleInputChange('location', event.target.value)}
                  placeholder="e.g. Stevenage, UK"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Job Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="temporary">Temporary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-white">Min Salary (£)</Label>
                <Input
                  type="number"
                  value={formData.minSalary}
                  onChange={(event) => handleInputChange('minSalary', event.target.value)}
                  placeholder="24000"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Max Salary (£)</Label>
                <Input
                  type="number"
                  value={formData.maxSalary}
                  onChange={(event) => handleInputChange('maxSalary', event.target.value)}
                  placeholder="32000"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Job Description</CardTitle>
            <CardDescription className="text-gray-400">Provide a detailed description of the role</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 rounded-lg border border-[#d2ac47]/30 bg-[#ba9036]/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 text-sm text-[#f7ef8a]">
                <Sparkles className="h-5 w-5" aria-hidden />
                Use AI to generate/enhance job description
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-[#d2ac47]/50 text-[#f7ef8a] hover:bg-[#ba9036]/10"
                onClick={generateWithAI}
                disabled={generating}
              >
                {generating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
            <Textarea
              value={formData.description}
              onChange={(event) => handleInputChange('description', event.target.value)}
              placeholder="Enter a detailed job description..."
              rows={6}
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Requirements</CardTitle>
            <CardDescription className="text-gray-400">Add key requirements and qualifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newRequirement}
                onChange={(event) => setNewRequirement(event.target.value)}
                placeholder="Add a requirement..."
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Button type="button" onClick={addRequirement} className="bg-gold-gradient text-black">
                Add
              </Button>
            </div>
            {formData.requirements.length > 0 && (
              <div className="space-y-2">
                {formData.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-gray-300">
                    <span>{requirement}</span>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeRequirement(index)} className="h-6 w-6 text-gray-400 hover:text-red-400">
                      <X className="h-3 w-3" aria-hidden />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Benefits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newBenefit}
                onChange={(event) => setNewBenefit(event.target.value)}
                placeholder="Add a benefit..."
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Button type="button" onClick={addBenefit} className="bg-gold-gradient text-black">
                Add
              </Button>
            </div>
            {formData.benefits.length > 0 && (
              <div className="space-y-2">
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-gray-300">
                    <span>{benefit}</span>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeBenefit(index)} className="h-6 w-6 text-gray-400 hover:text-red-400">
                      <X className="h-3 w-3" aria-hidden />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Special Features</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-[#d2ac47]/20 bg-gradient-to-r from-[#edc967]/10 to-purple-500/10 p-4">
              <Checkbox
                id="returner-friendly-edit"
                checked={formData.returnerFriendly}
                onCheckedChange={(checked) => handleInputChange('returnerFriendly', checked as boolean)}
              />
              <div className="space-y-1">
                <label htmlFor="returner-friendly-edit" className="flex items-center gap-2 text-sm text-white">
                  <Heart className="h-4 w-4 text-[#ba9036]" aria-hidden />
                  This job is returner-friendly
                </label>
                <p className="text-xs text-gray-400">Suitable for parents, carers, and those with employment gaps</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-white">
                <Award className="h-4 w-4 text-[#ba9036]" aria-hidden />
                Pre-Interview Skill Test (Optional)
              </Label>
              <Select value={formData.skillTestId} onValueChange={(value) => handleInputChange('skillTestId', value)}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="Select a skill test" />
                </SelectTrigger>
                <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                  <SelectItem value="none">None</SelectItem>
                  {skillTests.map((test) => (
                    <SelectItem key={test.id} value={String(test.id)}>
                      {test.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.skillTestId && formData.skillTestId !== 'none' && (
                <p className="flex items-center gap-1 text-xs text-[#ba9036]">
                  <Eye className="h-3 w-3" aria-hidden />
                  Candidates must complete this test before applying
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
        {showDelete && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => setIsDeleting(true)}
            className="order-2 bg-red-500/20 text-red-200 hover:bg-red-500/30 sm:order-1"
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden />
            Delete Job
          </Button>
        )}
        <div className={`order-1 flex flex-col gap-3 sm:flex-row sm:items-center ${showDelete ? 'sm:order-2' : ''}`}>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-white/20 text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="bg-gold-gradient hover:brightness-110 text-black"
          >
            <Save className="mr-2 h-4 w-4" aria-hidden />
            {primaryActionLabel ?? 'Save Changes'}
          </Button>
        </div>
      </div>

      {showDelete && isDeleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <Card className="mx-4 max-w-md border-red-500/50 bg-[#1a1d2e]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <AlertCircle className="h-5 w-5 text-red-400" aria-hidden />
                Delete Job Posting?
              </CardTitle>
              <CardDescription className="text-gray-400">
                This action cannot be undone. The job posting and all associated applications will be permanently deleted.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleting(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  className="flex-1 bg-red-500 text-white hover:bg-red-600"
                >
                  Delete Permanently
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
